import { useEffect } from "react";
import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useParams,
  type Location,
} from "react-router";
import { Toaster } from "sonner";
import { resolveEnvironmentNotchLabel } from "@runcheetah/shared";
import { EnvironmentNotch } from "@runcheetah/ui";

import { GlobalFailedPaymentBanner } from "@/components/billing/failed-payment-banner";
import { CenteredLoader } from "@/components/common";
import { FocusedEditorShell } from "@/components/layout/focused-editor-shell";
import DashboardLayout from "@/components/layout/dashboard-layout";
import ProtectedLayout from "@/components/layout/protected-layout";
import {
  buildBrandPath,
  isBrandReadyForApp,
  isBrandPathAvailableBeforeAppReady,
  useRouteBrand,
} from "@/hooks/use-route-brand";
import { BRAND_APP_ROUTES, PROTECTED_APP_ROUTES, PUBLIC_APP_ROUTES } from "@/lib/app-routes";
import { captureLandingWebsiteIntent } from "@/lib/landing-website-intent";
import { isRenderOnlyPath } from "@/lib/render-routes";
import CampaignPostRoutePage from "@/pages/campaign-post";
import PrivateBetaPage from "@/pages/private-beta";
import SocialPostRenderPage from "@/pages/social-post-render";
import StandalonePostRoutePage from "@/pages/standalone-post";

const environmentNotchLabel = resolveEnvironmentNotchLabel(
  import.meta.env.VITE_APP_ENV,
  import.meta.env.DEV,
);

const PROTECTED_INDEX_ROUTES = PROTECTED_APP_ROUTES.filter((route) => route.index);
const PROTECTED_SHELL_ROUTES = PROTECTED_APP_ROUTES.filter((route) => !route.index);

function AppChrome() {
  const location = useLocation();
  if (isRenderOnlyPath(location.pathname)) {
    return null;
  }

  return (
    <>
      <EnvironmentNotch label={environmentNotchLabel} />
      <Toaster position="bottom-right" richColors />
    </>
  );
}

function FocusedEditorRouteLayout({ modal = false }: { modal?: boolean }) {
  const { brandId } = useParams<{ brandId: string }>();

  return (
    <FocusedEditorShell modal={modal}>
      <GlobalFailedPaymentBanner brandId={brandId} />
      <Outlet />
    </FocusedEditorShell>
  );
}

function LandingWebsiteIntentCapture() {
  const location = useLocation();

  useEffect(() => {
    captureLandingWebsiteIntent(location.search);
  }, [location.search]);

  return null;
}

function BrandRouteAvailabilityGuard() {
  const location = useLocation();
  const { brandId, brand, onboarding, brandQuery } = useRouteBrand();

  if (brandQuery.isPending) {
    return <CenteredLoader />;
  }

  if (!brand) {
    return <Navigate to="/app" replace />;
  }

  if (
    !isBrandReadyForApp({
      status: brand.status,
      onboardingMode: brand.onboardingMode,
      firstAssetReady: onboarding?.completion.firstAssetReady,
    }) &&
    !isBrandPathAvailableBeforeAppReady({
      brandId,
      status: brand.status,
      pathname: location.pathname,
    })
  ) {
    return <Navigate to={buildBrandPath(brand.id, "/onboarding")} replace />;
  }

  return <Outlet />;
}

function AppRoutes() {
  const location = useLocation();
  const backgroundLocation = resolveBackgroundLocation(location);

  return (
    <>
      <Routes location={backgroundLocation ?? location}>
        {PUBLIC_APP_ROUTES.map((route) => (
          <Route key={route.id} path={route.path} element={route.element} />
        ))}

        <Route path="/render/social-post" element={<SocialPostRenderPage />} />

        <Route path="/private-beta" element={<ProtectedLayout releaseAccessRequired={false} />}>
          <Route index element={<PrivateBetaPage />} />
        </Route>

        <Route path="/app" element={<ProtectedLayout />}>
          {PROTECTED_INDEX_ROUTES.map((route) => (
            <Route key={route.id} index={route.index} element={route.element} />
          ))}
        </Route>

        <Route path="/app" element={<DashboardLayout />}>
          {PROTECTED_SHELL_ROUTES.map((route) => (
            <Route key={route.id} path={route.path} element={route.element} />
          ))}
        </Route>

        <Route path="/app/brands/:brandId/editor" element={<ProtectedLayout />}>
          <Route element={<FocusedEditorRouteLayout />}>
            <Route
              path="campaigns/:id/posts/:postId"
              element={<CampaignPostRoutePage mode="focused" />}
            />
            <Route
              path="posts/batches/:batchId/posts/:postId"
              element={<StandalonePostRoutePage mode="focused" />}
            />
          </Route>
        </Route>

        <Route path="/app/brands/:brandId" element={<DashboardLayout />}>
          <Route element={<BrandRouteAvailabilityGuard />}>
            {BRAND_APP_ROUTES.map((route) => (
              <Route key={route.id} path={route.path} element={route.element} />
            ))}
          </Route>
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {backgroundLocation ? (
        <Routes>
          <Route
            path="/app/brands/:brandId/editor"
            element={<FocusedEditorRouteLayout modal={true} />}
          >
            <Route
              path="campaigns/:id/posts/:postId"
              element={<CampaignPostRoutePage mode="focused" />}
            />
            <Route
              path="posts/batches/:batchId/posts/:postId"
              element={<StandalonePostRoutePage mode="focused" />}
            />
          </Route>
        </Routes>
      ) : null}
    </>
  );
}

function resolveBackgroundLocation(location: Location): Location | null {
  const state = location.state as { backgroundLocation?: Location } | null;
  return state?.backgroundLocation ?? null;
}

export default function App() {
  return (
    <BrowserRouter>
      <LandingWebsiteIntentCapture />
      <AppChrome />
      <AppRoutes />
    </BrowserRouter>
  );
}
